import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface DividerProps extends React.HTMLAttributes<HTMLDivElement> {
	type?: "horizontal" | "vertical";
	orientation?: "left" | "right" | "center";
	orientationMargin?: string | number;
	dashed?: boolean;
	plain?: boolean;
}

const Divider = forwardRef<HTMLDivElement, DividerProps>(
	(
		{
			className,
			type = "horizontal",
			orientation = "center",
			orientationMargin,
			dashed = false,
			plain = false,
			children,
			...props
		},
		ref,
	) => {
		if (type === "vertical") {
			return (
				<span
					ref={ref}
					className={cn(
						"inline-block align-middle mx-2 h-[0.9em] border-l",
						dashed
							? "border-dashed border-gray-300 dark:border-gray-600"
							: "border-solid border-gray-200 dark:border-gray-700",
						className,
					)}
					role="separator"
					{...props}
				/>
			);
		}

		// Horizontal divider
		const hasChildren = !!children;

		if (!hasChildren) {
			return (
				<div
					ref={ref}
					className={cn(
						"my-4 border-t",
						dashed
							? "border-dashed border-gray-300 dark:border-gray-600"
							: "border-solid border-gray-200 dark:border-gray-700",
						className,
					)}
					role="separator"
					{...props}
				/>
			);
		}

		const marginStyle =
			orientationMargin !== undefined
				? typeof orientationMargin === "number"
					? `${orientationMargin}px`
					: orientationMargin
				: undefined;

		return (
			<div
				ref={ref}
				className={cn("flex items-center my-4", className)}
				role="separator"
				{...props}
			>
				<span
					className={cn(
						"flex-shrink-0 border-t",
						dashed
							? "border-dashed border-gray-300 dark:border-gray-600"
							: "border-solid border-gray-200 dark:border-gray-700",
						orientation === "left" && "w-[5%]",
						orientation === "right" && "flex-1",
						orientation === "center" && "flex-1",
					)}
					style={
						orientation === "left" && marginStyle
							? { minWidth: marginStyle, width: marginStyle }
							: undefined
					}
				/>

				<span
					className={cn(
						"px-4 text-sm whitespace-nowrap",
						plain
							? "text-gray-700 dark:text-gray-200 font-normal"
							: "text-gray-500 dark:text-gray-400 font-medium",
					)}
				>
					{children}
				</span>

				<span
					className={cn(
						"flex-shrink-0 border-t",
						dashed
							? "border-dashed border-gray-300 dark:border-gray-600"
							: "border-solid border-gray-200 dark:border-gray-700",
						orientation === "right" && "w-[5%]",
						orientation === "left" && "flex-1",
						orientation === "center" && "flex-1",
					)}
					style={
						orientation === "right" && marginStyle
							? { minWidth: marginStyle, width: marginStyle }
							: undefined
					}
				/>
			</div>
		);
	},
);

Divider.displayName = "Divider";

export { Divider };
